import moment from 'moment';
import type { Theme } from '@mui/material/styles';
import type { AccountData } from '../../api/models/AccountData';
import type { DateAvailabilityOutMultipleOrderPages } from '../../api/models/DateAvailabilityOutMultipleOrderPages';
import type { PassDatedSessionSelectionDTO } from '../../api/models/PassDatedSessionSelectionDTO';
import { ScheduleSession, ScheduleSessionStatus } from '../../managePassesPage/hooks/usePassScheduleData';
import { getExperienceName } from '../../managePassesPage/utils/passUtils';
import { DATE_FORMAT } from '../../consts';
import { findOrderPageById } from '../passScheduleSelector/utils';

const DAYS_PER_WEEK = 7;
const SHORT_LAST_WEEK_MAX_DAYS = 3;

export type DatedSessionWeek = {
  index: number;
  from: string;
  to: string;
  sessions: ScheduleSession[];
};

export const datedSelectionKey = (selection: PassDatedSessionSelectionDTO): string =>
  `${selection.order_page_id}|${selection.date}|${selection.start_time}`;

export const sessionSelectionKey = (session: ScheduleSession): string =>
  datedSelectionKey({ order_page_id: session.orderPageId, date: session.date, start_time: session.timeSeconds });

export const toSessionSelection = (session: ScheduleSession): PassDatedSessionSelectionDTO => ({
  order_page_id: session.orderPageId,
  date: session.date,
  start_time: session.timeSeconds,
});

export const getStudioPrimary = (theme: Theme): { main: string; contrastText: string } => ({
  main: theme.customTheme?.palette?.primary ?? theme.palette.primary.main,
  contrastText: theme.customTheme?.palette?.text?.button?.contained ?? theme.palette.primary.contrastText,
});

export const buildDatedSessions = (
  availableDates: DateAvailabilityOutMultipleOrderPages[],
  account: AccountData,
  language: string,
  startDate: string,
  endDate: string,
): ScheduleSession[] => {
  const sessions: ScheduleSession[] = [];
  for (const orderPageAvailability of availableDates) {
    const orderPage = findOrderPageById(account, orderPageAvailability.order_page_id);
    const experience = account.experiences?.find((candidate) =>
      candidate.order_pages?.some((page) => page.id === orderPageAvailability.order_page_id),
    );
    if (orderPage && experience) {
      const workshopName = getExperienceName(account, orderPageAvailability.order_page_id, language);
      for (const dayAvailability of orderPageAvailability.availability) {
        if (dayAvailability.date >= startDate && dayAvailability.date <= endDate) {
          for (const slot of dayAvailability.times) {
            if (!slot.isDisabled) {
              sessions.push({
                date: dayAvailability.date,
                timeSeconds: slot.seconds,
                eventId: slot.eventId,
                orderPageId: orderPageAvailability.order_page_id,
                experienceId: experience.id,
                workshopName,
                duration: orderPage.duration,
                ticketsLeft: slot.ticketsLeft,
                totalTickets: slot.totalTickets,
                status: ScheduleSessionStatus.OPEN,
              });
            }
          }
        }
      }
    }
  }
  return sessions.sort((a, b) => a.date.localeCompare(b.date) || a.timeSeconds - b.timeSeconds);
};

export const groupSessionsByWeek = (
  sessions: ScheduleSession[],
  startDate: string,
  endDate: string,
): DatedSessionWeek[] => {
  const weeks: DatedSessionWeek[] = [];
  const lastDay = moment(endDate, DATE_FORMAT);
  for (
    let weekStart = moment(startDate, DATE_FORMAT);
    !weekStart.isAfter(lastDay);
    weekStart.add(DAYS_PER_WEEK, 'days')
  ) {
    const weekEnd = moment.min(moment(weekStart).add(DAYS_PER_WEEK - 1, 'days'), lastDay);
    const from = weekStart.format(DATE_FORMAT);
    const to = weekEnd.format(DATE_FORMAT);
    weeks.push({
      index: weeks.length,
      from,
      to,
      sessions: sessions.filter((session) => session.date >= from && session.date <= to),
    });
  }
  const lastWeek = weeks[weeks.length - 1];
  const previousWeek = weeks[weeks.length - 2];
  if (
    previousWeek &&
    moment(lastWeek.to, DATE_FORMAT).diff(moment(lastWeek.from, DATE_FORMAT), 'days') + 1 <= SHORT_LAST_WEEK_MAX_DAYS
  ) {
    weeks.splice(weeks.length - 2, 2, {
      ...previousWeek,
      to: lastWeek.to,
      sessions: [...previousWeek.sessions, ...lastWeek.sessions],
    });
  }
  return weeks;
};
